/**
 * BIP-39 mnemonic generation.
 *
 * Lifted from leodex-v2/src/features/wallet/keystore-creation.ts
 * but rewritten to use @scure/bip39 (no ethers dependency).
 *
 * Compatible word-for-word with the THORChain ecosystem and any
 * BIP-39 wallet (MetaMask, Phantom, Tonkeeper, etc).
 */

import { generateMnemonic, mnemonicToSeedSync, validateMnemonic } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english.js";

export type WordCount = 12 | 24;

/**
 * Generate a fresh BIP-39 mnemonic with the requested word count.
 * 12 words = 128 bits entropy, 24 words = 256 bits.
 */
export const generatePhrase = (wordCount: WordCount = 12): string => {
  const strength = wordCount === 24 ? 256 : 128;
  return generateMnemonic(wordlist, strength);
};

/** Validate that a user-provided phrase is a real BIP-39 mnemonic. */
export const isValidPhrase = (phrase: string): boolean => {
  return validateMnemonic(normalizePhrase(phrase), wordlist);
};

/**
 * Normalise a mnemonic: trim, lowercase, collapse whitespace to single spaces.
 * Matches what wallets like ASGARDEX do before encryption.
 */
export const normalizePhrase = (phrase: string): string => {
  return phrase.trim().toLowerCase().replace(/\s+/g, " ");
};

/**
 * BIP-39 mnemonic → 64-byte seed. Used as the master seed for HD derivation.
 * Synchronous (uses PBKDF2-HMAC-SHA512 via @noble/hashes, 2048 rounds).
 */
export const phraseToSeed = (phrase: string): Uint8Array => {
  return mnemonicToSeedSync(normalizePhrase(phrase));
};

/**
 * Pick N random positions (0-indexed) from the phrase for verification.
 * Used in the create-wallet flow's "type words 2, 7, 11 back" challenge.
 */
export const selectVerificationIndices = (wordCount: number, count = 3): number[] => {
  const max = Math.min(count, wordCount);
  const picked = new Set<number>();
  while (picked.size < max) {
    const buf = new Uint32Array(1);
    globalThis.crypto.getRandomValues(buf);
    picked.add(buf[0]! % wordCount);
  }
  return Array.from(picked).sort((a, b) => a - b);
};
